import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import { AccountBalance as AccountBalanceIcon, Download as DownloadIcon } from '@mui/icons-material';
import useExpenseLookups from '../../components/expense/useExpenseLookups';
import { formatDate } from '../../services/expenseVouchersApi';
import { accountsApi, formatMoney, PAYMENT_STATUS_META } from '../../services/expenseAccountsApi';
import { PaymentStatusChip, useAccountsBase } from '../../components/expense/voucherShared';
import {
  AccountsExpensesTabs,
  DateRangeFilter,
  EmployeeFilter,
  PaymentTypeFilter,
  useDebounced,
  CompactFilterBar,
} from '../../components/expense/AccountsFilters';

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'UNPROCESSED,IN_PROCESS,PARTIALLY_PAID,ON_HOLD', label: 'Open (not paid)' },
  ...Object.entries(PAYMENT_STATUS_META).map(([value, m]) => ({ value, label: m.label })),
];

// [label, sort key or null]
const COLUMNS = [
  ['Voucher No.', 'voucherNumber'],
  ['Approved', 'approvedAt'],
  ['Employee', 'employee'],
  ['Vendor', null],
  ['Head', null],
  ['Employment Country', null],
  ['Payment Type', null],
  ['Requested', null],
  ['Cost to Project', null],
  ['Net Payable', 'netPayable'],
  ['Paid', null],
  ['Balance', 'balance'],
  ['Status', 'paymentStatus'],
  ['Project', null],
];

// Accounts expense grid: approved vouchers with their processing state.
// Filtering, search, sorting and pagination all happen on the server.
const AccountsExpenseGrid = () => {
  const navigate = useNavigate();
  const base = useAccountsBase();
  const { countries, currencies, jobNames, users } = useExpenseLookups(['countries', 'currencies', 'jobNames', 'users']);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [vendor, setVendor] = useState('');
  const debouncedVendor = useDebounced(vendor.trim());
  const [filters, setFilters] = useState({
    paymentStatus: 'UNPROCESSED,IN_PROCESS,PARTIALLY_PAID,ON_HOLD',
    currency: '',
    countryId: '',
    jobNameId: '',
    employeeId: '',
    paymentType: '',
    approvedFrom: '',
    approvedTo: '',
  });
  const [sort, setSort] = useState({ sort: 'approvedAt', order: 'desc' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => { setPage(0); }, [debouncedSearch, debouncedVendor, filters, sort]);

  const params = { search: debouncedSearch, vendor: debouncedVendor, ...filters, ...sort };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await accountsApi.grid({ ...params, page: page + 1, limit: rowsPerPage });
      setRows(res.data || []);
      setTotal(res.pagination?.totalItems || 0);
      setError(null);
    } catch (e) {
      setRows([]);
      setError(e);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, debouncedVendor, filters, sort, page, rowsPerPage]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));
  const setFilterValue = (key) => (value) => setFilters((f) => ({ ...f, [key]: value }));
  const toggleSort = (key) => setSort((s) => ({ sort: key, order: s.sort === key && s.order === 'desc' ? 'asc' : 'desc' }));

  const exportCsv = async () => {
    try {
      await accountsApi.exportCsv(params);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const money = (row, field) => (typeof row[field] === 'number' ? formatMoney(row[field], row.currency) : '-');

  return (
    <Box sx={{ bgcolor: 'background.default', p: 3 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, fontSize: '30px', lineHeight: 1.5, letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountBalanceIcon color="primary" />
            Accounts Expenses
          </Typography>
          <Typography variant="body1" color="text.secondary">Approved vouchers: booking, payments and payment status.</Typography>
        </Box>
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportCsv} sx={{ textTransform: 'none' }}>Export CSV</Button>
      </Box>

      <AccountsExpensesTabs />

      <CompactFilterBar>
        <TextField size="small" placeholder="Search voucher, employee, vendor, head, project..." value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 300 }} />
        <TextField select size="small" label="Payment status" value={filters.paymentStatus} onChange={setFilter('paymentStatus')} sx={{ minWidth: 190 }}>
          {STATUS_FILTERS.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Currency" value={filters.currency} onChange={setFilter('currency')} sx={{ minWidth: 130 }}>
          <MenuItem value="">All</MenuItem>
          {currencies.map((c) => <MenuItem key={c.value} value={c.value}>{c.value}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Employment country" value={filters.countryId} onChange={setFilter('countryId')} sx={{ minWidth: 190 }}>
          <MenuItem value="">All</MenuItem>
          {countries.map((c) => <MenuItem key={c.value} value={c.value}>{c.raw?.name || c.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Project" value={filters.jobNameId} onChange={setFilter('jobNameId')} sx={{ minWidth: 170 }}>
          <MenuItem value="">All</MenuItem>
          {jobNames.map((j) => <MenuItem key={j.value} value={j.value}>{j.label}</MenuItem>)}
        </TextField>
        <EmployeeFilter users={users} value={filters.employeeId} onChange={setFilterValue('employeeId')} />
        <TextField size="small" label="Vendor" placeholder="Vendor name" value={vendor} onChange={(e) => setVendor(e.target.value)} sx={{ minWidth: 170 }} />
        <PaymentTypeFilter value={filters.paymentType} onChange={setFilterValue('paymentType')} />
        <DateRangeFilter
          label="Approved"
          from={filters.approvedFrom}
          to={filters.approvedTo}
          onFromChange={setFilterValue('approvedFrom')}
          onToChange={setFilterValue('approvedTo')}
        />
      </CompactFilterBar>

      {error && <Alert severity="error" sx={{ mb: 2 }}>Failed to load vouchers: {error.message}</Alert>}

      <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              {COLUMNS.map(([label, key]) => (
                <TableCell key={label} sx={{ color: 'white', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {key ? (
                    <TableSortLabel
                      active={sort.sort === key}
                      direction={sort.sort === key ? sort.order : 'desc'}
                      onClick={() => toggleSort(key)}
                      sx={{ color: 'white !important', '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                    >
                      {label}
                    </TableSortLabel>
                  ) : label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={COLUMNS.length} align="center"><CircularProgress size={28} /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 4 }}><Typography variant="body2" color="text.secondary">No vouchers match.</Typography></TableCell></TableRow>
            ) : rows.map((row) => (
              <TableRow key={row._id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`${base}/${row._id}`)}>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {row.voucherNumber}
                  {row.isSettlement && <Chip size="small" label="Settlement" sx={{ ml: 0.5 }} />}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(row.approvedAt)}</TableCell>
                <TableCell>{row.employeeName}<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{row.department}</Typography></TableCell>
                <TableCell>{row.vendors.join(', ') || '-'}</TableCell>
                <TableCell>{row.heads.join(', ') || '-'}</TableCell>
                <TableCell>{row.employmentCountries.join(', ') || '-'}</TableCell>
                <TableCell>{row.paymentTypes.join(', ') || '-'}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{money(row, 'requestedAmount')}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{money(row, 'costToProject')}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                  {money(row, 'netPayable')}
                  {/* Net of the wallet; fixed when the first payment posts the wallet */}
                  {row.netPayable !== null && row.netPayable !== undefined && row.walletPosted === false && row.paymentStatus !== 'CANCELLED' && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 400 }}>estimated</Typography>
                  )}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.netPayable === null ? '-' : money(row, 'paidTotal')}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{money(row, 'balancePayable')}</TableCell>
                <TableCell><PaymentStatusChip status={row.paymentStatus} settledVia={row.settledVia} /></TableCell>
                <TableCell>{row.jobName || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>
    </Box>
  );
};

export default AccountsExpenseGrid;
